import * as React from "react";
import Mainlayout from "../../../components/layouts/MainLayout";
import PaneStatus from "../../../components/feedback/PaneStatus";
import { Alert } from "../../../components/ui/alert";
import { useJyloDocument, DocumentStatus } from "../hooks/useJyloDocument";
import PlaybookAssemblyWrapper from "./PlaybookAssemblyWrapper";
import SaveAssemblyButton from "./SaveAssemblyButton";
import { AssemblyApiContext } from "../types/assembly-context";

interface ReferencesProps {
  modeBar?: React.ReactNode;
}

/** Builds the API context from a detected document, or null if it is not a usable Jylo assembly. */
const buildAssemblyContext = (status: DocumentStatus): AssemblyApiContext | null => {
  if (!status.isJylo || !status.source || !status.assemblyObjectIdentifier) return null;

  if (status.source === "playbook" && status.playbookObjectIdentifier) {
    return {
      source: "playbook",
      assemblyObjectIdentifier: status.assemblyObjectIdentifier,
      playbookObjectIdentifier: status.playbookObjectIdentifier,
    };
  }

  if (status.source === "flow" && status.projectObjectIdentifier && status.flowObjectIdentifier) {
    return {
      source: "flow",
      assemblyObjectIdentifier: status.assemblyObjectIdentifier,
      projectObjectIdentifier: status.projectObjectIdentifier,
      flowObjectIdentifier: status.flowObjectIdentifier,
    };
  }

  return null;
};

const References = ({ modeBar }: ReferencesProps) => {
  const { documentStatus, isLoading: checkingDocumentStatus } = useJyloDocument();

  const assemblyContext = React.useMemo(
    () => buildAssemblyContext(documentStatus),
    [documentStatus]
  );

  return (
    <Mainlayout
      modeBar={modeBar}
      // In the mode row rather than the header: the header already has "Save" (to
      // Jylo) and a second Save beside it would be two buttons with one word and
      // different meanings.
      modeBarEnd={<SaveAssemblyButton ctx={assemblyContext} label="Save assembly" />}
    >
      {checkingDocumentStatus && <PaneStatus loading title="Reading the document…" />}
      {assemblyContext && <PlaybookAssemblyWrapper ctx={assemblyContext} />}
      {documentStatus.checked && !assemblyContext && (
        <Alert severity="warning" className="m-4">
          This is not a Jylo Assembly document. Please open a Jylo document to use the reference
          features.
        </Alert>
      )}
    </Mainlayout>
  );
};

export default References;
